import type { Film } from "@/types/film";

interface DetailContentProps {
  film: Film;
}

export default function DetailContent({ film }: DetailContentProps) {
  const paragraphs = film.synopsis.split("\n\n");
  const crew = [
    { name: film.director, role: "导演" },
    ...film.cast.map((name, index) => ({ name, role: index === 0 ? "主演" : "演员" })),
  ];

  return (
    <div className="space-y-8">
      <section id="story" className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
        <div className="flex items-center gap-2">
          <span className="h-[3px] w-5 rounded-full bg-accent-500" />
          <h2 className="text-lg font-bold text-foreground-950 md:text-xl">剧情介绍</h2>
        </div>
        <div className="mt-4 space-y-4">
          {paragraphs.map((paragraph, index) => (
            <p
              key={paragraph.slice(0, 12)}
              className={[
                "text-base leading-8 text-foreground-700 md:text-[15px]",
                index === 0
                  ? "first-letter:float-left first-letter:mr-2 first-letter:font-heading first-letter:text-4xl first-letter:font-black first-letter:leading-[1.1] first-letter:text-primary-700"
                  : "",
              ].join(" ")}
            >
              {paragraph}
            </p>
          ))}
        </div>
        <p className="mt-5 border-t border-background-200 pt-4 text-[11px] leading-6 text-foreground-500">
          以上剧情简介由本站编辑整理，包含前情设定与部分情节走向，介意剧透请谨慎阅读。
        </p>
      </section>

      <section id="cast" className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-[3px] w-5 rounded-full bg-accent-500" />
            <h2 className="text-lg font-bold text-foreground-950 md:text-xl">演员与幕后</h2>
          </div>
          <span className="text-[11px] text-foreground-500">共 {crew.length} 位</span>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {crew.map((person) => (
            <div
              key={`${person.name}-${person.role}`}
              className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100/60 px-3 py-2.5"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                {person.name.slice(0, 1)}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-foreground-950">
                  {person.name}
                </span>
                <span className="mt-0.5 block text-[11px] text-foreground-500">{person.role}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      {film.reviews.length > 0 && (
        <section id="reviews" className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-[3px] w-5 rounded-full bg-accent-500" />
              <h2 className="text-lg font-bold text-foreground-950 md:text-xl">影评精选</h2>
            </div>
            <span className="text-[11px] text-foreground-500">{film.reviews.length} 篇</span>
          </div>

          <div className="mt-5 space-y-4">
            {film.reviews.map((review) => (
              <article
                key={review.title}
                className="rounded-md border border-background-200 bg-background-100/50 p-4 md:p-5"
              >
                <div className="flex items-start gap-4">
                  <div className="flex w-11 shrink-0 flex-col items-center rounded-md bg-background-50 px-1 py-2">
                    <span className="font-label text-lg leading-none text-accent-600">
                      {review.score.toFixed(1)}
                    </span>
                    <span className="mt-1 text-[10px] text-foreground-500">评分</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[15px] font-semibold leading-snug text-foreground-950">
                      {review.title}
                    </h3>
                    <p className="mt-2 text-[13px] leading-6 text-foreground-600">{review.excerpt}</p>
                    <p className="mt-2.5 flex flex-wrap items-center gap-x-2 text-[11px] text-foreground-500">
                      <span className="text-primary-700">{review.author}</span>
                      <span className="h-2.5 w-px bg-background-300" />
                      <span>{review.role}</span>
                      <span className="h-2.5 w-px bg-background-300" />
                      <span>影评示例内容</span>
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}